import { Injectable } from '@angular/core';
import { erc20Abi, neonWrapper2Abi, SPLToken } from '@neonevm/token-transfer-core';
import { createAppKit, AppKit } from '@reown/appkit';
import { EthersAdapter } from '@reown/appkit-adapter-ethers';
import { CaipNetwork } from '@reown/appkit-common';

import { BrowserProvider, TransactionReceipt, TransactionRequest, TransactionResponse } from 'ethers';
import { BehaviorSubject, delay, filter, from, map, Observable, ReplaySubject, Subject, switchMap, tap } from 'rxjs';
import { Big } from 'big.js';
import { environment } from '../../environments/environment';
import {
  DataStorage,
  EXCLUDED_WALLETS,
  metadata,
  NEON_WALLETS,
  neonBalance,
  neonEthersDevnet,
  neonEthersMainnet,
  tokenBalance
} from '../../utils';
import { NotificationService } from '../../notifications';
import { Address, ConnectionState, NeonTokenBalance, NeonWallet } from '../../models';

@Injectable({ providedIn: 'root' })
export class WalletConnectService extends DataStorage<any> {
  projectId: string = environment.walletConnect.projectId;
  address$: BehaviorSubject<Address> = new BehaviorSubject<Address>(undefined!);
  balance$: BehaviorSubject<Big> = new BehaviorSubject<Big>(new Big(0));
  wallet$: BehaviorSubject<NeonWallet> = new BehaviorSubject<NeonWallet>(undefined!);
  chainId$: BehaviorSubject<number> = new BehaviorSubject<number>(environment.chainId!);
  provider: BrowserProvider | undefined;
  provider$: ReplaySubject<BrowserProvider> = new ReplaySubject<BrowserProvider>(0);
  refresh$: Subject<boolean> = new Subject<boolean>();
  connected$ = new BehaviorSubject<boolean>(false);
  isSameNetwork$ = new ReplaySubject<boolean>(0);
  chains: CaipNetwork[] = [neonEthersDevnet, neonEthersMainnet];
  modal: AppKit;
  private _snapshot: ConnectionState;
  private _pendingTimeout = 3e5;
  private _eip6963: { info: { name: string; rdns: string }; provider: any }[] = [];
  private _lastSelectedWallet?: string;
  private _addNetworkInFlight = false;

  get address(): `0x${string}` {
    return this.address$.value;
  }

  get addressView$(): Observable<string> {
    return this.address$.pipe(map(d => {
      if (d?.length) {
        return `${d.slice(0, 7)}..${d.slice(-5)}`;
      }
      return '';
    }));
  }

  get chainId(): number {
    return this.chainId$.value;
  }

  get publicClient(): any {
    return this.provider?.getSigner();
  }

  setProvider = (p: any) => {
    if (p) {
      const ethersProvider = new BrowserProvider(p);
      this.provider = ethersProvider;
      this.provider$.next(ethersProvider);
    }
  };

  neonBalance(): Observable<NeonTokenBalance> {
    return from(neonBalance(this.provider));
  }

  wNeonBalance(token: SPLToken): Observable<NeonTokenBalance> {
    return from(tokenBalance(this.provider, token, neonWrapper2Abi));
  }

  tokenBalance(token: SPLToken): Observable<NeonTokenBalance> {
    return from(tokenBalance(this.provider, token, erc20Abi));
  }

  signTransaction = async (transaction: TransactionRequest): Promise<TransactionResponse> => {
    const signer = await this.provider!.getSigner();
    return signer.sendTransaction(transaction);
  };

  getTransactionReceipt = async (transaction: TransactionResponse): Promise<TransactionReceipt | null> => {
    await transaction.wait(1, this._pendingTimeout);
    return this.provider!.getTransactionReceipt(transaction.hash);
  };

  isSameNetworkEmit(): void {
    this.subs.push(this.chainId$.pipe(tap(chainId => {
      this.isSameNetwork$.next(chainId ? Number(chainId) === environment.chainId : true);
    })).subscribe());
  }

  providerNotifications(data: ConnectionState): void {
    const { provider, isConnected, chainId } = data;
    if (provider && isConnected) {
      const hasNetworkChanged = this._snapshot?.chainId && this._snapshot?.chainId !== chainId;
      this.n.success({
        title: hasNetworkChanged ? 'Network changed' : 'Neon wallet connected',
      });
    }
    if (!isConnected && this._snapshot?.isConnected !== isConnected) {
      this.n.success({ title: 'Neon wallet disconnected' });
    }
    this._snapshot = { ...data };
  }

  open(): void {
    this.modal.open();
  }

  signMessage(message: string): Observable<string> {
    return new Observable<string>(subscriber => {
      const signer = this.provider!.getSigner()
        .then(signer => signer.signMessage(message))
        .then(hex => subscriber.next(hex))
        .catch(e => subscriber.next(e))
        .finally(() => subscriber.complete());
    });
  }

  walletBalance(address: Address): Observable<Big> {
    return from(this.provider!.getBalance(address)).pipe(map((b: unknown) => {
      const balance = new Big(typeof b === 'bigint' ? b.toString() : 0);
      this.balance$.next(balance);
      return balance;
    }));
  }

  walletBalanceClean(): Observable<Big> {
    this.balance$.next(new Big(0));
    return this.balance$.pipe(delay(100));
  }

  async disconnect(): Promise<any> {
    await this.modal.disconnect();
  }

  clearConnection(): void {
    this.connected$.next(false);
    this.provider = undefined;
    this.address$.next(undefined!);
  }

  private listenEip6963(): void {
    window.addEventListener('eip6963:announceProvider', (e: any) => {
      const detail = e.detail;
      if (detail?.info?.rdns && !this._eip6963.find(p => p.info.rdns === detail.info.rdns)) {
        this._eip6963.push(detail);
      }
    });
    window.dispatchEvent(new Event('eip6963:requestProvider'));
  }

  private async addNetworkToWallet(network: CaipNetwork, walletName?: string): Promise<boolean> {
    const lower = walletName?.toLowerCase();
    const item = lower
      ? this._eip6963.find(p => p.info.name.toLowerCase().includes(lower))
      : this._eip6963[0];
    if (!item) return false;
    const params = {
      chainId: `0x${Number(network.id).toString(16)}`,
      chainName: String(network.name),
      nativeCurrency: {
        name: String(network.nativeCurrency.name),
        symbol: String(network.nativeCurrency.symbol),
        decimals: Number(network.nativeCurrency.decimals)
      },
      rpcUrls: Array.from(network.rpcUrls.default.http, String),
      blockExplorerUrls: network.blockExplorers?.default?.url
        ? [String(network.blockExplorers.default.url)]
        : []
    };
    try {
      await item.provider.request({ method: 'wallet_addEthereumChain', params: [params] });
      return true;
    } catch (e) {
      console.error('[WC] wallet_addEthereumChain failed', e);
      return false;
    }
  }

  private async handleSwitchNetworkFailure(network: CaipNetwork): Promise<void> {
    if (this._addNetworkInFlight) return;
    this._addNetworkInFlight = true;
    try {
      const ok = await this.addNetworkToWallet(network, this._lastSelectedWallet);
      if (ok) {
        this.n.success({
          title: `${network.name} added to wallet`,
          message: 'Click Connect again to finish connecting.'
        });
        setTimeout(() => this.modal.open(), 600);
      } else {
        this.n.error({
          title: 'Failed to add network',
          message: `Please add ${network.name} to your wallet manually (chainId ${network.id}, RPC ${network.rpcUrls.default.http[0]}) and try again.`
        });
      }
    } finally {
      setTimeout(() => { this._addNetworkInFlight = false; }, 2000);
    }
  }

  init(): void {
    const projectId = this.projectId;
    this.listenEip6963();

    const defaultNetwork = environment.chainId === neonEthersMainnet.id ? neonEthersMainnet : neonEthersDevnet;
    const networks: [CaipNetwork, CaipNetwork] = defaultNetwork.id === neonEthersMainnet.id
      ? [neonEthersMainnet, neonEthersDevnet]
      : [neonEthersDevnet, neonEthersMainnet];

    this.modal = createAppKit({
      adapters: [new EthersAdapter()],
      networks,
      defaultNetwork,
      metadata,
      projectId,
      enableEIP6963: true,
      allWallets: 'HIDE',
      featuredWalletIds: NEON_WALLETS.filter(d => d.name !== 'Taho').map(w => w.id),
      includeWalletIds: NEON_WALLETS.map((w) => w.id),
      excludeWalletIds: EXCLUDED_WALLETS,
      enableWalletConnect: true,
      features: {
        email: false,
        socials: false,
        swaps: false,
        onramp: false
      }
    })

    this.modal.subscribeProviders((data: any): void => {
      let provider, isConnected, chainId;
      if(data.eip155) {
        provider = data.eip155;
        isConnected = true;
        chainId = Number(this.modal.getChainId());
        const address = this.modal.getAddress();
        this.setProvider(data.eip155);
        this.connected$.next(true);
        this.chainId$.next(chainId);
        this.address$.next(address as Address);
      } else {
        this.clearConnection();
      }
      this.providerNotifications({ provider, isConnected: !!isConnected, chainId });
    });

    this.modal.subscribeEvents?.((event: any): void => {
      const name = event?.data?.event || event?.event;
      const props = event?.data?.properties ?? event?.properties;
      if (name === 'SELECT_WALLET' && props?.name) {
        this._lastSelectedWallet = props.name;
      }
      if (name === 'CONNECT_ERROR' && /switch network/i.test(props?.message ?? '')) {
        void this.handleSwitchNetworkFailure(defaultNetwork);
      }
    });

    this.subs.push(this.address$.pipe(switchMap(address => address?.length ?
      this.walletBalance(<Address>address) : this.walletBalanceClean())).subscribe());
    this.subs.push(this.refresh$.pipe(filter(() => !!this.address), switchMap(() => {
      return this.walletBalance(<Address>this.address);
    })).subscribe());
    this.isSameNetworkEmit();
  }

  refresh(): void {
    this.refresh$.next(true);
  }

  constructor(private n: NotificationService) {
    super();
  }
}
